import React from 'react';
import { Image, PenTool, ArrowLeft, Sparkles, Layers } from 'lucide-react';
import { GenerationMode } from '../types';

interface ModeSelectionProps {
  imageUrl: string;
  onSelectMode: (mode: GenerationMode) => void;
  onBack: () => void;
}

const ModeSelection: React.FC<ModeSelectionProps> = ({ imageUrl, onSelectMode, onBack }) => {
  return (
    <div className="flex flex-col h-full bg-black animate-fade-in">
      {/* Header */}
      <div className="h-16 bg-slate-900 border-b border-slate-800 flex items-center px-6 shrink-0">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white font-medium transition-colors"
        >
          <ArrowLeft size={18} />
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Image Preview */}
        <div className="flex-1 flex items-center justify-center p-8 bg-slate-950">
          <div className="relative max-w-xl w-full">
            <div className="absolute inset-0 bg-teal-500/10 blur-3xl rounded-full"></div>
            <img 
              src={imageUrl} 
              alt="Uploaded sketch" 
              className="relative w-full h-auto rounded-xl shadow-2xl ring-1 ring-white/10"
            />
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-slate-800 text-slate-400 text-xs px-3 py-1.5 rounded-full border border-slate-700">
              Your uploaded sketch
            </div>
          </div>
        </div>

        {/* Mode Selection Panel */}
        <div className="w-full lg:w-[480px] bg-slate-900 border-t lg:border-t-0 lg:border-l border-slate-800 p-6 lg:p-8 flex flex-col">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Output Type</h2>
            <p className="text-slate-400">
              Select how you want your floor plan to be generated.
            </p>
          </div>

          <div className="flex-1 space-y-4">
            {/* Option A: Visual Staging */}
            <button
              onClick={() => onSelectMode('visual')}
              className="w-full p-6 bg-gradient-to-br from-slate-800 to-slate-800/50 hover:from-teal-900/30 hover:to-teal-800/20 border-2 border-slate-700 hover:border-teal-500 rounded-2xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-teal-500/20 rounded-xl group-hover:bg-teal-500/30 transition-colors">
                  <Image size={28} className="text-teal-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white">Visual Staging</h3>
                    <span className="px-2 py-0.5 bg-teal-500/20 text-teal-400 text-xs font-medium rounded-full">
                      Recommended
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">
                    Generate a beautiful, presentation-ready floor plan image with textures and furniture.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md flex items-center gap-1">
                      <Sparkles size={12} />
                      AI-Generated
                    </span>
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md">
                      High Quality Image
                    </span>
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md">
                      10-15 Credits
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Option B: Editable CAD */}
            <button
              onClick={() => onSelectMode('vector')}
              className="w-full p-6 bg-gradient-to-br from-slate-800 to-slate-800/50 hover:from-amber-900/30 hover:to-amber-800/20 border-2 border-slate-700 hover:border-amber-500 rounded-2xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-500/20 rounded-xl group-hover:bg-amber-500/30 transition-colors">
                  <PenTool size={28} className="text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white">Editable CAD</h3>
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
                      Beta
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">
                    Generate vector-based floor plan data that you can edit, adjust measurements, and export.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md flex items-center gap-1">
                      <Layers size={12} />
                      Editable Layers
                    </span>
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md">
                      Adjustable Dimensions
                    </span>
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-md">
                      5 Credits
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Info Footer */}
          <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              Both options use AI to analyze your sketch. Visual Staging creates a polished image, 
              while Editable CAD gives you interactive control over walls and measurements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeSelection;
